import { fn } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { set } from 'ember-set-helper';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { gt } from 'ember-truth-helpers';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import notEq from 'ember-truth-helpers/helpers/not-eq';
import or from 'ember-truth-helpers/helpers/or';
import lowerCase from 'lodash/lowerCase';
import BvCheckbox from 'volta/components/bv-checkbox';
import BvDndHandle from 'volta/components/bv-dnd/handle';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvRadio from 'volta/components/bv-radio';
import BvTag from 'volta/components/bv-tag';
import BvTextField from 'volta/components/bv-text-field';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import { sanitized } from 'volta/utils/text-utils';

import type { ICollectionGroupOption } from 'volta/models/types/collection-view';

export interface CollectionViewGroupsSignature {
  Args: {
    options: ICollectionGroupOption[];
    groupLimit?: number;
    groupMin?: number;
    disabled?: boolean;
    onChange: (group: ICollectionGroupOption, checked: boolean, e: MouseEvent) => void;
    onReorder?: (groupBy: ICollectionGroupOption[]) => void;
  };
  Element: HTMLDivElement;
}

export default class CollectionViewGroupsComponent extends Component<CollectionViewGroupsSignature> {
  @tracked search?: string;

  resourceName = { singular: 'attribute', plural: 'attributes' };
  groupName = `CollectionViewGroups_${guidFor(this)}`;

  handleSortEndAction = (sortOptions: ICollectionGroupOption[]) => {
    this.args.onReorder?.([...(sortOptions ?? [])]);
  };

  get filteredOptions() {
    const { search } = this;

    if (!search || search === '') {
      return this.args.options;
    }
    const _search = sanitized(search);
    return this.args.options.filter((p) => p.label && sanitized(p.label).includes(_search));
  }

  get isGroupMin() {
    if (!this.args.groupMin) {
      return false;
    }

    return this.args.options.filter((option) => option.selected).length <= this.args.groupMin;
  }

  <template>
    <div class='collection-view-groups' ...attributes>
      <div class='u-p-1'>
        <BvTextField
          class='u-flex-fill'
          @labelHidden={{true}}
          @placeholder={{t 'search'}}
          @value={{this.search}}
          @prefix={{component BvIcon glyph='search'}}
          @onChange={{set this 'search'}}
          @autoFocus={{true}}
          @clearButton={{gt this.search.length 0}}
          @onClearButtonClick={{set this 'search' undefined}}
        />
      </div>
      <div
        {{sortableGroup groupName=this.groupName onChange=this.handleSortEndAction direction='y'}}
      >
        {{#each this.filteredOptions key='value' as |option|}}
          {{#let
            (or (not option.selected) (not option.editable) @disabled)
            (component BvTag text=option.label color=option.color size='sm')
            as |dndDisabled label|
          }}
            <BvInline
              class='u-py-1 u-px-1 u-bg-surface'
              @blockAlign='center'
              @wrap={{false}}
              @gap='1'
              {{sortableItem groupName=this.groupName model=option disabled=dndDisabled}}
            >
              <BvDndHandle @disabled={{dndDisabled}} />
              {{#if (notEq @groupLimit 1)}}
                <BvCheckbox
                  @label={{label}}
                  @checked={{option.selected}}
                  @disabled={{or
                    @disabled
                    (not option.editable)
                    (and option.selected this.isGroupMin)
                  }}
                  @onChange={{fn @onChange option}}
                  @labelClass='u-overflow-hidden'
                  class='u-overflow-hidden'
                />
              {{else}}
                <BvRadio
                  @label={{label}}
                  @checked={{option.selected}}
                  @disabled={{or @disabled (not option.editable)}}
                  @onChange={{fn @onChange option}}
                  @labelClass='u-overflow-hidden'
                  class='u-overflow-hidden'
                />
              {{/if}}
            </BvInline>
          {{/let}}
        {{else}}
          <BvEmptySearchResult
            @title={{t
              'bvList.emptySearchResultTitle'
              resourceNamePlural=(lowerCase (t 'properties'))
            }}
            class='u-p-2'
          />
        {{/each}}
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Groups': typeof CollectionViewGroupsComponent;
    'collection-view/groups': typeof CollectionViewGroupsComponent;
  }
}
